/**
 * Quiz Validation Utilities
 *
 * Centralized validation functions that ensure consistency
 * between frontend and backend quiz data structures.
 */
import { QUESTION_TYPES, EDUCATION_REGIONS, VALID_QUESTION_TYPES } from '../constants/subjects';
/** Strip HTML for comparing MC option text to stored correctAnswer (rich-text options). */
function stripHtmlForCompare(s) {
    return s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}
/**
 * Match server `extractMcLetter`: letter prefix or single letter (A–D).
 */
export function extractMcLetterClient(val) {
    if (val === undefined || val === null)
        return '';
    const s = String(val).trim();
    const m = s.match(/^([ABCD])(?:[\).\s]|$)/i);
    if (m)
        return m[1].toUpperCase();
    if (/^[ABCD]$/i.test(s))
        return s.toUpperCase();
    return '';
}
function normalizeMultipleChoiceCorrectLetter(raw) {
    const ca = raw.correctAnswer;
    const opts = [
        ['A', String(raw.answerA ?? '')],
        ['B', String(raw.answerB ?? '')],
        ['C', String(raw.answerC ?? '')],
        ['D', String(raw.answerD ?? '')],
    ];
    const fromLetter = extractMcLetterClient(ca);
    if (fromLetter) {
        const pair = opts.find(([L]) => L === fromLetter);
        if (pair && stripHtmlForCompare(pair[1]).length > 0)
            return fromLetter;
    }
    const caNorm = stripHtmlForCompare(String(ca ?? ''));
    if (caNorm) {
        for (const [letter, text] of opts) {
            if (!text)
                continue;
            if (stripHtmlForCompare(text) === caNorm)
                return letter;
        }
    }
    const firstWithText = opts.find(([, t]) => stripHtmlForCompare(t).length > 0);
    return firstWithText ? firstWithText[0] : 'A';
}
function normalizeTrueFalseStored(val) {
    if (val === true || val === false)
        return val ? 'true' : 'false';
    const s = String(val ?? '')
        .trim()
        .toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes')
        return 'true';
    if (s === 'false' || s === '0' || s === 'no')
        return 'false';
    return 'true';
}
export function sanitizeSkillCodes(raw) {
    if (!Array.isArray(raw))
        return undefined;
    const codes = [
        ...new Set(raw
            .map((x) => String(x).trim().slice(0, 200))
            .filter((s) => s.length > 0)),
    ].slice(0, 24);
    return codes.length ? codes : undefined;
}
function attachLearningProfileFields(base, raw) {
    const codes = sanitizeSkillCodes(raw.skill_codes);
    if (codes?.length)
        base.skill_codes = codes;
    if (typeof raw.skill_label === 'string') {
        const sl = raw.skill_label.trim().slice(0, 200);
        if (sl)
            base.skill_label = sl;
    }
    for (const k of ['question_modality', 'scaffolding_default', 'difficulty_band', 'bloom_level']) {
        if (typeof raw[k] === 'string') {
            const v = raw[k].trim().slice(0, 64);
            if (v)
                base[k] = v;
        }
    }
    if (raw.target_year_group != null && String(raw.target_year_group).trim()) {
        base.target_year_group = String(raw.target_year_group).trim().slice(0, 32);
    }
}
/**
 * Build a clean, backend-safe question object per type (stale fields from type switches removed).
 * Multiple-choice `correctAnswer` is stored as A|B|C|D so grading matches the student UI (A/B/C radios).
 */
export function normalizeQuestionForQuizSave(raw) {
    const id = String(raw.id ?? '');
    const typeRaw = String(raw.type ?? 'multiple_choice').replace(/-/g, '_');
    const type = VALID_QUESTION_TYPES.includes(typeRaw)
        ? typeRaw
        : QUESTION_TYPES.MULTIPLE_CHOICE;
    const question = String(raw.question ?? '');
    const difficulty = ['easy', 'medium', 'hard'].includes(String(raw.difficulty))
        ? raw.difficulty
        : 'medium';
    const points = typeof raw.points === 'number' && raw.points >= 1 ? raw.points : 1;
    const base = {
        id,
        type,
        question,
        difficulty,
        points,
    };
    if (raw.metadata !== null && typeof raw.metadata === 'object' && !Array.isArray(raw.metadata)) {
        base.metadata = raw.metadata;
    }
    attachLearningProfileFields(base, raw);
    switch (type) {
        case 'multiple_choice': {
            const answerA = String(raw.answerA ?? '');
            const answerB = String(raw.answerB ?? '');
            const answerC = String(raw.answerC ?? '');
            const answerD = String(raw.answerD ?? '');
            const correctAnswer = normalizeMultipleChoiceCorrectLetter({
                ...raw,
                answerA,
                answerB,
                answerC,
                answerD,
            });
            return {
                ...base,
                answerA,
                answerB,
                answerC,
                ...(answerD.trim() !== '' ? { answerD } : {}),
                correctAnswer,
            };
        }
        case 'true_false':
            return {
                ...base,
                correctAnswer: normalizeTrueFalseStored(raw.correctAnswer),
            };
        case 'short_answer':
            return {
                ...base,
                correctAnswer: String(raw.correctAnswer ?? ''),
            };
        case 'open_ended': {
            const out = {
                ...base,
                correctAnswer: String(raw.correctAnswer ?? ''),
            };
            if (raw.guidance != null)
                out.guidance = String(raw.guidance);
            if (Array.isArray(raw.keyConcepts))
                out.keyConcepts = raw.keyConcepts;
            if (raw.gradingKeywords !== null && typeof raw.gradingKeywords === 'object' && !Array.isArray(raw.gradingKeywords)) {
                out.gradingKeywords = raw.gradingKeywords;
            }
            return out;
        }
        case 'scenario_based': {
            const out = {
                ...base,
                scenario: String(raw.scenario ?? ''),
                correctAnswer: String(raw.correctAnswer ?? ''),
            };
            if (raw.guidance != null)
                out.guidance = String(raw.guidance);
            if (Array.isArray(raw.keyConcepts))
                out.keyConcepts = raw.keyConcepts;
            if (raw.gradingKeywords !== null && typeof raw.gradingKeywords === 'object' && !Array.isArray(raw.gradingKeywords)) {
                out.gradingKeywords = raw.gradingKeywords;
            }
            return out;
        }
        case 'fill_in_gap': {
            const sentence = String(raw.sentence ?? raw.text ?? '').trim() ||
                (question.includes('___') ? question : '');
            return {
                ...base,
                sentence,
                correctAnswer: String(raw.correctAnswer ?? ''),
            };
        }
        case 'match_answers':
            return {
                ...base,
                answerA: String(raw.answerA ?? ''),
                answerB: String(raw.answerB ?? ''),
                answerC: String(raw.answerC ?? ''),
                answerD: String(raw.answerD ?? ''),
                answerE: String(raw.answerE ?? ''),
                answerF: String(raw.answerF ?? ''),
                ...(raw.correctAnswer != null && String(raw.correctAnswer).trim() !== ''
                    ? { correctAnswer: String(raw.correctAnswer).trim().toLowerCase() }
                    : {}),
            };
        case 'answer_question': {
            const out = {
                ...base,
                correctAnswer: String(raw.correctAnswer ?? ''),
            };
            if (raw.guidance != null)
                out.guidance = String(raw.guidance);
            if (Array.isArray(raw.keyConcepts))
                out.keyConcepts = raw.keyConcepts;
            if (raw.gradingKeywords !== null && typeof raw.gradingKeywords === 'object' && !Array.isArray(raw.gradingKeywords)) {
                out.gradingKeywords = raw.gradingKeywords;
            }
            return out;
        }
        default:
            return {
                ...base,
                correctAnswer: String(raw.correctAnswer ?? ''),
            };
    }
}
export function normalizeQuizTemplateQuestionsForSave(questions) {
    if (!Array.isArray(questions))
        return [];
    return questions.map((q) => normalizeQuestionForQuizSave(q !== null && typeof q === 'object' ? q : {}));
}
/**
 * When the tutor changes question type, reset fields so we do not send incompatible keys (e.g. MC options on a true/false row).
 */
export function buildQuestionOnTypeChange(newType, prev) {
    const id = String(prev.id ?? '');
    const question = String(prev.question ?? '');
    const difficulty = ['easy', 'medium', 'hard'].includes(String(prev.difficulty))
        ? prev.difficulty
        : 'medium';
    const points = typeof prev.points === 'number' && prev.points >= 1 ? prev.points : 1;
    const typeRaw = newType.replace(/-/g, '_');
    const type = VALID_QUESTION_TYPES.includes(typeRaw)
        ? typeRaw
        : QUESTION_TYPES.MULTIPLE_CHOICE;
    const learningCarry = {};
    attachLearningProfileFields(learningCarry, prev);
    const base = { id, question, difficulty, points, type, ...learningCarry };
    switch (type) {
        case 'multiple_choice':
            return {
                ...base,
                answerA: '',
                answerB: '',
                answerC: '',
                answerD: '',
                correctAnswer: 'A',
            };
        case 'true_false':
            return { ...base, correctAnswer: 'true' };
        case 'short_answer':
            return { ...base, correctAnswer: '' };
        case 'open_ended':
            return { ...base, correctAnswer: '', guidance: '' };
        case 'scenario_based':
            return { ...base, scenario: '', correctAnswer: '', guidance: '' };
        case 'fill_in_gap':
            return { ...base, sentence: '', correctAnswer: '' };
        case 'match_answers':
            return {
                ...base,
                answerA: '',
                answerB: '',
                answerC: '',
                answerD: '',
                answerE: '',
                answerF: '',
                correctAnswer: '',
            };
        case 'answer_question':
            return { ...base, correctAnswer: '' };
        default:
            return { ...base, correctAnswer: '' };
    }
}
// Question type normalization function
export function normalizeQuestionType(type) {
    return type.replace(/-/g, '_');
}
/**
 * Validate a quiz generation request
 */
export function validateQuizGenerationRequest(request) {
    const errors = [];
    // Required field validations
    if (!request.region) {
        errors.push({ field: 'region', message: 'Region is required' });
    }
    else if (!Object.values(EDUCATION_REGIONS).includes(request.region)) {
        errors.push({ field: 'region', message: 'Invalid region' });
    }
    if (!request.level) {
        errors.push({ field: 'level', message: 'Level is required' });
    }
    if (!request.subject) {
        errors.push({ field: 'subject', message: 'Subject is required' });
    }
    if (!request.subcategory) {
        errors.push({ field: 'subcategory', message: 'Subcategory is required' });
    }
    if (!request.questionType) {
        errors.push({ field: 'questionType', message: 'Question type is required' });
    }
    else if (!VALID_QUESTION_TYPES.includes(request.questionType)) {
        errors.push({ field: 'questionType', message: 'Invalid question type' });
    }
    // Optional field validations
    if (request.qualityThreshold !== undefined) {
        if (typeof request.qualityThreshold !== 'number' ||
            request.qualityThreshold < 0 ||
            request.qualityThreshold > 1) {
            errors.push({
                field: 'qualityThreshold',
                message: 'Quality threshold must be a number between 0 and 1'
            });
        }
    }
    if (request.validationLevel !== undefined) {
        const validLevels = ['basic', 'comprehensive', 'expert'];
        if (!validLevels.includes(request.validationLevel)) {
            errors.push({
                field: 'validationLevel',
                message: 'Invalid validation level'
            });
        }
    }
    return {
        isValid: errors.length === 0,
        errors
    };
}
// Legacy normalization function removed - use QUESTION_TYPES constants directly
/**
 * Normalize region to standard format
 */
export function normalizeRegion(region) {
    const regionMappings = {
        'uk': EDUCATION_REGIONS.UK,
        'us': EDUCATION_REGIONS.US,
        'uae': EDUCATION_REGIONS.UAE,
        'canada': EDUCATION_REGIONS.CANADA,
        'australia': EDUCATION_REGIONS.AUSTRALIA,
        'international': EDUCATION_REGIONS.INTERNATIONAL
    };
    return regionMappings[region.toLowerCase()] || region;
}
/**
 * Validate question response data structure
 */
export function validateQuestionResponse(response) {
    const errors = [];
    if (!response) {
        errors.push({ field: 'response', message: 'Response is required' });
        return { isValid: false, errors };
    }
    if (!response.success) {
        errors.push({ field: 'success', message: 'Response indicates failure' });
    }
    if (!response.data) {
        errors.push({ field: 'data', message: 'Response data is required' });
        return { isValid: false, errors };
    }
    const data = response.data;
    if (!data.id) {
        errors.push({ field: 'id', message: 'Question ID is required' });
    }
    if (!data.question) {
        errors.push({ field: 'question', message: 'Question object is required' });
    }
    else {
        if (!data.question.rawText && !data.question.formatted?.question) {
            errors.push({ field: 'question.text', message: 'Question text is required' });
        }
        if (!data.question.type) {
            errors.push({ field: 'question.type', message: 'Question type is required' });
        }
    }
    if (!data.metadata) {
        errors.push({ field: 'metadata', message: 'Question metadata is required' });
    }
    return {
        isValid: errors.length === 0,
        errors
    };
}
/**
 * Create a standardized error response
 */
export function createErrorResponse(message, code, details) {
    return {
        success: false,
        error: {
            message,
            code: code || 'VALIDATION_ERROR',
            details
        }
    };
}
/**
 * Create a standardized success response
 */
export function createSuccessResponse(data, metadata) {
    return {
        success: true,
        data,
        ...(metadata && { metadata })
    };
}
export default {
    validateQuizGenerationRequest,
    validateQuestionResponse,
    normalizeRegion,
    createErrorResponse,
    createSuccessResponse
};
